// src/quality-management/pages/ProjectAnalyticsDashboard.tsx
// Refatorado com a nova identidade visual (Tailwind + UI kit) e recharts theme-aware

import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthAdapter';
import { SidebarPortal } from '../../shared/components/SidebarPortal';
import { apiService } from '../services/api';
import { DashboardStats } from '../services/api/reports.service';
import { Task } from '../services/api/types';
import { summarizeSchedule, SCHEDULE_ORDER, SCHEDULE_STYLES } from '../utils/taskSchedule';
import CreateProjectModal from '../components/CreateProjectModal';
import { Button, Card, Badge, Spinner, EmptyState, Table, Column } from '../../shared/components/ui';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

// Lê as CSS variables do tema atual e devolve cores RGB legíveis em claro/escuro
interface ChartColors {
  grid: string;
  axis: string;
  text: string;
  brand: string;
  success: string;
  warning: string;
  danger: string;
  info: string;
}

const getChartColors = (): ChartColors => {
  const s = getComputedStyle(document.documentElement);
  const v = (name: string) => `rgb(${s.getPropertyValue(name).trim()})`;
  return {
    grid: v('--border'),
    axis: v('--foreground-muted'),
    text: v('--foreground-muted'),
    brand: v('--brand'),
    success: v('--success'),
    warning: v('--warning'),
    danger: v('--danger'),
    info: v('--info'),
  };
};

const ProjectAnalyticsDashboard: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [colors, setColors] = useState<ChartColors>(getChartColors);

  // Recalcula as cores dos gráficos quando a classe 'dark' do <html> muda
  useEffect(() => {
    const observer = new MutationObserver(() => setColors(getChartColors()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Check if user can create projects (managers, directors, admins)
  const canCreateProject = user?.role && (
    ['manager', 'director', 'admin', 'project manager'].includes(user.role.toLowerCase()) ||
    user.role.toLowerCase().includes('manager') ||
    user.role.toLowerCase().includes('director') ||
    user.role.toLowerCase().includes('admin') ||
    user.role === 'Project Manager'
  );

  // Handle project creation
  const handleProjectCreated = async () => {
    setIsProjectModalOpen(false);
    // Refresh analytics data
    fetchAnalyticsData();
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [data, taskList] = await Promise.all([
        apiService.reports.getAnalyticsDashboard(),
        apiService.tasks.getTasks().catch(() => []),
      ]);
      setStats(data);
      setTasks(Array.isArray(taskList) ? taskList : []);
    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError('Não foi possível carregar os dados de análise. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!stats) return;

    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "General Statistics\n";
    csvContent += "Metric,Value\n";
    csvContent += `Total Projects,${stats.totalStats.totalProjects}\n`;
    csvContent += `Total Hours,${stats.totalStats.totalHours}\n`;
    csvContent += `Total Employees,${stats.totalStats.totalEmployees}\n\n`;

    csvContent += "Project Details\n";
    csvContent += "Project,Total Hours,Completion %,Team\n";
    stats.projects.forEach(project => {
      csvContent += `"${project.name}",${project.totalHours},${project.completionPercentage}%,${project.employeeCount}\n`;
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `analytics_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sidebarContent = canCreateProject ? (
    <Button fullWidth onClick={() => setIsProjectModalOpen(true)} className="mb-4">
      Novo Projeto
    </Button>
  ) : null;

  // Loading state
  if (loading) {
    return (
      <div className="mx-auto max-w-[1200px] px-4 py-8">
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4">
          <Spinner size="lg" />
          <p className="text-base text-foreground-muted">Carregando análises...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="mx-auto max-w-[1200px] px-4 py-8">
        <Card padding="lg" className="text-center">
          <EmptyState
            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4M12 17h.01" /></svg>}
            title="Erro ao Carregar Dados"
            description={error}
            action={
              <Button onClick={fetchAnalyticsData}>Tentar Novamente</Button>
            }
          />
        </Card>
      </div>
    );
  }

  // No data state
  if (!stats) {
    return (
      <div className="mx-auto max-w-[1200px] px-4 py-8">
        <Card padding="lg" className="text-center">
          <EmptyState
            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><rect x="7" y="11" width="3" height="6" /><rect x="13" y="7" width="3" height="10" /></svg>}
            title="Nenhum dado disponível"
            description="Não há dados de análise para exibir."
          />
        </Card>
      </div>
    );
  }

  // Chart data
  const projectHoursData = stats.projects.map(p => ({
    name: p.name.length > 15 ? p.name.substring(0, 15) + '...' : p.name,
    'Total Hours': p.totalHours,
    'Expected Hours': p.expectedHours,
  }));

  const employeeHoursData = stats.employees.slice(0, 8).map(e => ({
    name: e.name.split(' ')[0],
    'Hours': e.totalHours,
  }));

  const completionRateData = stats.projects.map(p => ({
    name: p.name.length > 12 ? p.name.substring(0, 12) + '...' : p.name,
    'Completion': p.completionPercentage,
  }));

  // Paleta para séries multi-cor (Cell)
  const piePalette = [colors.brand, colors.info, colors.success, colors.warning, colors.danger];

  const overtimeStatusData = [
    { name: 'Aguardando Supervisor', value: stats.overtime.pendingSupervisorApprovals },
    { name: 'Aguardando Gerente', value: stats.overtime.pendingManagerApprovals },
    { name: 'Aprovado', value: stats.overtime.approvedOvertime },
    { name: 'Rejeitado', value: stats.overtime.rejectedOvertime },
  ].filter(item => item.value > 0);

  const tooltipStyle = {
    backgroundColor: 'rgb(var(--surface-1))',
    border: `1px solid ${colors.grid}`,
    borderRadius: '12px',
    color: 'rgb(var(--foreground))',
    fontSize: '0.875rem',
  };
  const axisTick = { fill: colors.axis, fontSize: 12 };

  const completionTone = (pct: number): 'success' | 'warning' | 'danger' =>
    pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'danger';

  // Situação de prazo das tarefas (mesma regra do Plano de Entregas)
  const scheduleSummary = summarizeSchedule(tasks);
  const scheduleTotal = SCHEDULE_ORDER.reduce((s, k) => s + scheduleSummary[k], 0);

  const projectColumns: Column<typeof stats.projects[number]>[] = [
    { key: 'name', header: 'Projeto', render: (p) => <span className="font-medium text-foreground">{p.name}</span> },
    { key: 'totalHours', header: 'Horas', render: (p) => <span className="tabular-nums">{p.totalHours}h</span> },
    {
      key: 'completion',
      header: 'Conclusão',
      render: (p) => (
        <Badge tone={completionTone(p.completionPercentage)}>{p.completionPercentage}%</Badge>
      ),
    },
    { key: 'employeeCount', header: 'Equipe', render: (p) => <span className="tabular-nums">{p.employeeCount}</span> },
  ];

  const employeeColumns: Column<typeof stats.employees[number]>[] = [
    { key: 'name', header: 'Colaborador', render: (e) => <span className="font-medium text-foreground">{e.name}</span> },
    { key: 'department', header: 'Depto', render: (e) => e.department },
    { key: 'totalHours', header: 'Horas', render: (e) => <span className="tabular-nums">{e.totalHours}h</span> },
    { key: 'projectCount', header: 'Projetos', render: (e) => <span className="tabular-nums">{e.projectCount}</span> },
  ];

  return (
    <>
      <SidebarPortal>{sidebarContent}</SidebarPortal>
        <div className="mx-auto max-w-[1200px] px-4 py-8" data-testid="analytics-dashboard">

          {/* Page Header */}
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-display text-2xl font-semibold text-foreground">Análise de projetos</h1>
              <p className="mt-1 text-[15px] text-foreground-muted">
                Métricas consolidadas de desempenho da equipe e dos projetos
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={exportToExcel}
              leftIcon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
              }
            >
              Exportar CSV
            </Button>
          </div>

          {/* KPIs */}
          <Card padding="none" className="mb-6 overflow-hidden">
            <div className="grid grid-cols-2 divide-border lg:grid-cols-4 lg:divide-x [&>*]:border-b [&>*]:border-border lg:[&>*]:border-b-0">
              {[
                { label: 'Total de projetos', value: `${stats.totalStats.totalProjects}`, tone: 'text-brand', bg: 'bg-brand/10', icon: <path d="M4 7a2 2 0 0 1 2-2h3l2 2h7a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" /> },
                { label: 'Total de horas', value: `${stats.totalStats.totalHours}h`, tone: 'text-success', bg: 'bg-success/10', icon: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></> },
                { label: 'Colaboradores', value: `${stats.totalStats.totalEmployees}`, tone: 'text-info', bg: 'bg-info/10', icon: <path d="M16 21v-2a4 4 0 0 0-3-3.87M8 21v-2a4 4 0 0 1 3-3.87M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" /> },
                { label: 'Horas extras', value: `${stats.totalStats.totalOvertimeHours}h`, tone: 'text-warning', bg: 'bg-warning/10', icon: <><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2 2M5 3 2 6M22 6l-3-3" /></> },
              ].map((kpi) => (
                <div key={kpi.label} className="flex items-center gap-4 p-6">
                  <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${kpi.bg} ${kpi.tone}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{kpi.icon}</svg>
                  </span>
                  <div className="min-w-0">
                    <div className="font-display text-2xl font-semibold tabular-nums text-foreground">{kpi.value}</div>
                    <div className="truncate text-sm text-foreground-muted">{kpi.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Situação de prazo das tarefas */}
          <Card padding="lg" className="mb-6">
            <div className="mb-4 flex items-center justify-between gap-4">
              <h3 className="font-display text-base font-semibold text-foreground">Situação de prazo das tarefas</h3>
              <span className="text-sm text-foreground-muted tabular-nums">{scheduleTotal} tarefas</span>
            </div>
            {scheduleTotal > 0 ? (
              <>
                <div className="mb-4 flex h-3 w-full overflow-hidden rounded-full bg-surface-3">
                  {SCHEDULE_ORDER.map((state) => {
                    const v = scheduleSummary[state];
                    if (!v) return null;
                    return (
                      <div
                        key={state}
                        className={SCHEDULE_STYLES[state].bar}
                        style={{ width: `${(v / scheduleTotal) * 100}%` }}
                        title={`${SCHEDULE_STYLES[state].label}: ${v}`}
                      />
                    );
                  })}
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                  {SCHEDULE_ORDER.map((state) => (
                    <div key={state} className="flex items-center gap-2.5 rounded-lg border border-border bg-surface-2 px-3 py-2.5">
                      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${SCHEDULE_STYLES[state].bar}`} />
                      <div className="min-w-0">
                        <div className="font-display text-lg font-semibold tabular-nums text-foreground">{scheduleSummary[state]}</div>
                        <div className="truncate text-xs text-foreground-muted">{SCHEDULE_STYLES[state].label}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-foreground-muted">Nenhuma tarefa com datas para avaliar.</p>
            )}
          </Card>

          {/* Charts Row 1 */}
          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Hours by Project */}
            <Card padding="lg" className="lg:col-span-2">
              <h3 className="mb-4 font-display text-base font-semibold text-foreground">Horas por projeto</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={projectHoursData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
                  <XAxis dataKey="name" tick={axisTick} stroke={colors.axis} />
                  <YAxis tick={axisTick} stroke={colors.axis} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: colors.grid, opacity: 0.3 }} />
                  <Legend wrapperStyle={{ color: colors.text, fontSize: 12 }} />
                  <Bar dataKey="Expected Hours" name="Horas previstas" fill={colors.success} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Total Hours" name="Horas totais" fill={colors.brand} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* Overtime Status */}
            <Card padding="lg">
              <h3 className="mb-4 font-display text-base font-semibold text-foreground">Status de horas extras</h3>
              {overtimeStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={overtimeStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={86}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {overtimeStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={piePalette[index % piePalette.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ color: colors.text, fontSize: 12 }} verticalAlign="bottom" />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-[280px] items-center justify-center text-base text-foreground-muted">
                  Nenhuma hora extra registrada
                </div>
              )}
            </Card>
          </div>

          {/* Charts Row 2 */}
          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Hours by Employee */}
            <Card padding="lg">
              <h3 className="mb-4 font-display text-base font-semibold text-foreground">Horas por colaborador</h3>
              {employeeHoursData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={employeeHoursData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
                    <XAxis dataKey="name" tick={axisTick} stroke={colors.axis} />
                    <YAxis tick={axisTick} stroke={colors.axis} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: colors.grid, opacity: 0.3 }} />
                    <Bar dataKey="Hours" fill={colors.info} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-[280px] items-center justify-center text-base text-foreground-muted">
                  Nenhum colaborador com horas registradas
                </div>
              )}
            </Card>

            {/* Completion Rate */}
            <Card padding="lg">
              <h3 className="mb-4 font-display text-base font-semibold text-foreground">Taxa de conclusão por projeto</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={completionRateData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tick={axisTick} stroke={colors.axis} />
                  <YAxis dataKey="name" type="category" tick={axisTick} stroke={colors.axis} width={100} />
                  <Tooltip
                    formatter={(value) => [`${value}%`, 'Conclusão']}
                    contentStyle={tooltipStyle}
                    cursor={{ fill: colors.grid, opacity: 0.3 }}
                  />
                  <Bar dataKey="Completion" fill={colors.brand} radius={[0, 4, 4, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Tables */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Projects Table */}
            <Card padding="lg">
              <h3 className="mb-4 font-display text-base font-semibold text-foreground">Detalhes dos projetos</h3>
              <Table
                columns={projectColumns}
                data={stats.projects}
                rowKey={(p) => p.id}
                empty="Nenhum projeto encontrado."
              />
            </Card>

            {/* Employees Table */}
            <Card padding="lg">
              <h3 className="mb-4 font-display text-base font-semibold text-foreground">Estatísticas de colaboradores</h3>
              <Table
                columns={employeeColumns}
                data={stats.employees}
                rowKey={(e) => e.id}
                empty="Nenhum colaborador encontrado."
              />
            </Card>
          </div>
        </div>

      {/* Create Project Modal */}
      {isProjectModalOpen && (
        <CreateProjectModal
          onClose={() => setIsProjectModalOpen(false)}
          onProjectCreated={handleProjectCreated}
        />
      )}
    </>
  );
};

export default ProjectAnalyticsDashboard;
